import React, { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Button } from "antd";
import { ArrowLeft, Sparkle } from "@phosphor-icons/react";

import AnalysisReport from "../../components/AIPane/AnalysisReport";
import { parseCitationPage } from "../../utils/citationJump";
import { buildPathWithAIPaneTab, getMatterIdFromPath } from "../../utils/aiPaneUrl";
import { openBriefCitationInMatterWindow } from "../../utils/briefReaderWindowBridge";
import {
  brand,
  border,
  radius,
  spacing,
  surface,
  text,
  typography,
} from "../../theme/tokens";

const BriefReaderPage = styled.main.attrs({
  className: "intentional-scroll",
  "data-testid": "brief-reader-page",
})`
  height: 100vh;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: ${surface.page};
  color: ${text.primary};
  font-family: ${typography.fontFamily};
  padding: ${spacing[6]};
  box-sizing: border-box;
`;

const BriefReaderShell = styled.section`
  width: min(980px, 100%);
  margin: 0 auto;
`;

const BriefReaderHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[5]};
`;

const BriefReaderTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const BriefReaderKicker = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${brand.primary};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.bold};
  letter-spacing: 0.6px;
  text-transform: uppercase;
`;

const BriefReaderTitle = styled.h1`
  margin: 0;
  color: ${text.primary};
  font-size: 30px;
  line-height: 1.2;
  font-weight: ${typography.weight.semibold};
`;

const BriefReaderSubtitle = styled.p`
  margin: 0;
  color: ${text.secondary};
  font-size: ${typography.size.md};
  line-height: 1.5;
`;

const BriefReaderCard = styled.article`
  background: ${surface.surface};
  border: 1px solid ${border.light};
  border-radius: ${radius.lg};
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.08);
  padding: ${spacing[6]};
`;

const encodePathSegment = (value) => encodeURIComponent(String(value));

export const buildBriefCitationTarget = ({ parsed, report, fallbackMatterId } = {}) => {
  if (!parsed || parsed.shape === "legacy-anchor") return null;

  if (parsed.shape === "new") {
    const matterId = parsed.matterId ?? fallbackMatterId ?? report?.matter;
    if (matterId == null || parsed.docId == null || parsed.page == null) {
      return null;
    }
    const params = new URLSearchParams();
    params.set("page", String(parsed.page));
    if (parsed.snippet) params.set("q", parsed.snippet);
    return `/matter/${encodePathSegment(matterId)}/D${encodePathSegment(
      parsed.docId
    )}?${params.toString()}`;
  }

  const pageInfo = parseCitationPage(parsed);
  const matterId = fallbackMatterId ?? report?.matter;
  const documentId = report?.document;
  if (!pageInfo || matterId == null || documentId == null) return null;
  const params = new URLSearchParams();
  params.set("page", String(pageInfo.page));
  return `/matter/${encodePathSegment(matterId)}/D${encodePathSegment(
    documentId
  )}?${params.toString()}`;
};

const openInNewTab = (target) => {
  if (!target) return false;
  if (typeof window === "undefined" || typeof window.open !== "function") {
    return false;
  }
  window.open(target, "_blank", "noopener,noreferrer");
  return true;
};

const BriefReader = () => {
  const { id: matterId, jobId } = useParams();
  const navigate = useNavigate();

  const handleBackToMatter = () => {
    if (!matterId) return;
    navigate(`/matter/${matterId}?ai=analysis`);
  };

  const handleCitationClick = useCallback(
    (parsed, report) => {
      const target = buildBriefCitationTarget({
        parsed,
        report,
        fallbackMatterId: matterId,
      });
      if (!target) return;

      const targetWithAIPane = buildPathWithAIPaneTab(target, "analysis");
      const targetMatterId = getMatterIdFromPath(targetWithAIPane) ?? matterId;
      const opened = openBriefCitationInMatterWindow({
        matterId: targetMatterId,
        target: targetWithAIPane,
      });
      if (!opened) {
        openInNewTab(targetWithAIPane);
      }
    },
    [matterId]
  );

  return (
    <BriefReaderPage>
      <BriefReaderShell>
        <BriefReaderHeader>
          <BriefReaderTitleGroup>
            <BriefReaderKicker>
              <Sparkle size={15} weight="fill" aria-hidden="true" />
              AI Brief
            </BriefReaderKicker>
            <BriefReaderTitle>The Brief</BriefReaderTitle>
            <BriefReaderSubtitle>
              A static reading view for the completed Brief. Source links open
              in your matter window when it is available, or in a new tab.
            </BriefReaderSubtitle>
          </BriefReaderTitleGroup>
          <Button
            icon={<ArrowLeft size={15} weight="bold" />}
            onClick={handleBackToMatter}
            disabled={!matterId}
            data-testid="brief-reader-back"
          >
            Back to matter
          </Button>
        </BriefReaderHeader>

        <BriefReaderCard data-testid="brief-reader-card">
          <AnalysisReport
            jobId={jobId}
            variant="standalone"
            showReaderAction={false}
            fallbackMatterId={matterId}
            onCitationClick={handleCitationClick}
          />
        </BriefReaderCard>
      </BriefReaderShell>
    </BriefReaderPage>
  );
};

export default BriefReader;
